import { useFlags } from 'launchdarkly-react-client-sdk';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import React, { useContext, useEffect, useState } from 'react';
import Data from '../data';
import Grid from '../grid';
import Modal from '../modal';
import { MessageModal } from '../modal/MessageModal.component';
import UI from '../ui';
import Spinner from '../ui/Spinner.component';
import Utils from '../utils';
import Util from '../utils/Util';
import AddNewCarrier from './AddNewCarrier.component';
import { ConnectUSPSBusinessAccount } from './ConnectUSPSBusinessAccount.component';
import { AddFedExAccount } from './FedEx/AddFedExAccount.component';
import { FedExDisconnect } from './FedEx/FedExDisconnect.component';
import { FedExPendingMessage } from './FedEx/message/FedExPendingMessage.component';
import { FedExResellerMessage } from './FedEx/message/FedExResellerMessage.component';
import AddUpsCarrier from './UPS/AddUpsCarrier.component';
import { UspsBusinessBanner, useUspsBusiness } from './USPSBusiness';

const { ModalContext, StatusModal } = Modal;
const { GridPanel } = Grid;
const {
	AllCarriers,
	Carrier,
	Capability,
	InternalCapability,
	SegmentEventTypes,
} = Data;
const { Communication, ErrorCodes, Analytics, Session, useConstructor } = Utils;
const { WrappedTooltip, WrappedToolbar, Section, SectionBody } = UI;

const allCarriers = AllCarriers.reduce(
	(_result, carrier) => ({
		..._result,
		[carrier.value]: carrier,
	}),
	{}
);

const ManageCarrierAction = Object.freeze({
	null: null,
	add: 'add',
	edit: 'edit',
	delete: 'delete',
});

// Custom cell renderer for carrier column with USPS icon and tooltip
const CarrierCellRenderer = ({ data }) => {
	const { carrier } = data || {};
	const { displayName, value } = carrier || {};

	if (value === Carrier.USPSBusiness) {
		return (
			<div>
				<span>{displayName}</span>
				<i className="usps-business-help-icon" />
			</div>
		);
	}

	return <span>{displayName}</span>;
};

/**
 * Determines if the FedEx Reseller banner should be shown based on account creation date and current date.
 * @param {string} accountCreationDate - The account creation date
 * @returns {boolean} True if the banner should be shown
 */
const shouldShowFedExResellerBanner = accountCreationDate => {
	if (!accountCreationDate) {
		return false;
	}
	const LAUNCH_DATE = new Date('2025-11-13');
	const LAUNCH_END_DATE = new Date('2026-02-13'); // 3 months from Nov 13
	const ageOfAccount = Utils.Util.getAgeOfAccountInDays(accountCreationDate);
	const today = new Date();

	// For existing accounts (created before launch): show until 3 months after launch
	// For new accounts (created after launch): show for first 90 days of account
	const isExistingAccount = new Date(accountCreationDate) < LAUNCH_DATE;
	return isExistingAccount ? today < LAUNCH_END_DATE : ageOfAccount < 90;
};

export const ManageCarrier = ({
	addCarrierCallback = () => {},
	noEligibleCarriersCallback = () => {},
	enterpriseLocation = {},
	enterpriseLocations = null,
}) => {
	const { canAddFedexReseller } = useFlags();
	const carrierType = Util.getQueryParamByName('carrier')?.toLowerCase();
	const code = Util.getQueryParamByName('code');
	const authState = Util.getQueryParamByName('auth_state');
	const actionFromQuery = Util.getQueryParamByName('action')?.toLowerCase();

	const initialAction =
		actionFromQuery in ManageCarrierAction && carrierType in allCarriers
			? actionFromQuery
			: null;

	const [action, setAction] = useState(initialAction);
	// eslint-disable-next-line no-unused-vars
	const [selectedCarrier, setSelectedCarrier] = useState(
		allCarriers[carrierType]
	);
	const [capable, setCapable] = useState([]);
	const [configured, setConfigured] = useState([Carrier.USPS]);
	const [subscriberId, setSubscriberId] = useState(null);
	const [entLocation, setEntLocation] = useState(enterpriseLocation);
	const [carrierDetails, setCarrierDetails] = useState(null);

	const [isFedExResellerBannerVisible, setIsFedExResellerBannerVisible] =
		useState(false);
	const [isFedexPendingBannerVisible, setIsFedexPendingBannerVisible] =
		useState(false);
	const [isFedExAddFlow, setIsFedExAddFlow] = useState(
		carrierType === Carrier.FedEx && action === 'add'
	);

	const { showModal, showError } = useContext(ModalContext);
	const {
		canConnectToUspsBusiness,
		isEligible: isUspsBusinessEligible,
		onboardingStatus: uspsOnboardingStatus,
		userInfoToken,
		uspsBannerType,
		shouldShowUspsBusiness,
		isLoading: isUspsBusinessCheckInProgress,
		error: uspsBusinessError,
		setError: setUspsBusinessError,
		redirectUri: uspsRedirectUri,
		refetch,
		showUspsBusiness,
		hideUspsBusiness,
		clearUserInfoToken,
	} = useUspsBusiness();

	const hideUspsBusinessConnect = () => {
		hideUspsBusiness();
		setAction(null);
		setSelectedCarrier(null);
	};

	const carrierRowData = carrierArray =>
		carrierArray.map(carrier => ({ carrier: allCarriers[carrier] }));

	const setCapableAndConfiguredCarriers = location => {
		const isEnterpriseLocation = !isEmpty(location);
		const accountInfo = isEnterpriseLocation
			? location
			: Session.getAccountInfo();
		const configuredCarriers = isArray(accountInfo.configuredCarriers)
			? accountInfo.configuredCarriers
			: [];

		const combinedCapabilities = isEnterpriseLocation
			? [...location.Capabilities, ...location.InternalCapabilities]
			: [
					...accountInfo.capabilities,
					...accountInfo.internalAccountDetails.internalCapabilities,
				];
		const hasAccessToGlobalPost =
			combinedCapabilities.includes(Capability.Globalpost) ||
			combinedCapabilities.includes(Capability.GpSmartSaver);
		const capableCarriersArray = [];
		const configuredCarriersArray = [
			Carrier.USPS,
			...configuredCarriers.map(({ carrier }) => carrier),
		];

		if (
			canAddFedexReseller &&
			combinedCapabilities.includes(InternalCapability.FedexWallet)
		) {
			setIsFedexPendingBannerVisible(
				configuredCarriers.some(
					({ carrier, status }) =>
						carrier === Carrier.FedEx && status === 'PendingApproval'
				)
			);
		}

		if (
			canConnectToUspsBusiness &&
			isUspsBusinessEligible &&
			!configuredCarriersArray.includes(Carrier.USPSBusiness)
		) {
			configuredCarriersArray.push(Carrier.USPSBusiness);
		}

		const configuredCarriersMap = configuredCarriers.reduce(
			(_configuredCarriers, carrier) => {
				_configuredCarriers[carrier.carrier] = carrier;
				return _configuredCarriers;
			},
			{}
		);

		setCarrierDetails(configuredCarriersMap);

		if (hasAccessToGlobalPost) {
			configuredCarriersArray.push(Carrier.GlobalPost);
		}

		if (
			combinedCapabilities.includes(Capability.Ups) &&
			!configuredCarriersArray.includes(Carrier.UPS)
		) {
			capableCarriersArray.push(Carrier.UPS);
		}

		if (
			combinedCapabilities.includes(Capability.DhlExpress) &&
			!configuredCarriersArray.includes(Carrier.DHLExpress)
		) {
			capableCarriersArray.push(Carrier.DHLExpress);
		}

		if (
			combinedCapabilities.includes(Capability.Fedex) &&
			((canAddFedexReseller &&
				combinedCapabilities.includes(InternalCapability.FedexWallet)) ||
				combinedCapabilities.includes(InternalCapability.FedexDirect)) &&
			!configuredCarriersArray.includes(Carrier.FedEx)
		) {
			capableCarriersArray.push(Carrier.FedEx);

			if (
				combinedCapabilities.includes(InternalCapability.FedexWallet) &&
				shouldShowFedExResellerBanner(accountInfo.customerDetails?.creationDate)
			) {
				setIsFedExResellerBannerVisible(true);
			}
		} else {
			setIsFedExResellerBannerVisible(false);
		}

		// Bulk update but no Carriers are able to be added
		if (!isEmpty(enterpriseLocations) && capableCarriersArray.length === 0) {
			noEligibleCarriersCallback(false);
		}

		setCapable(capableCarriersArray);

		const filteredConfiguredCarriers = configuredCarriersArray.filter(
			carrier => {
				if (carrier === Carrier.FedEx) {
					return (
						(canAddFedexReseller &&
							combinedCapabilities.includes(InternalCapability.FedexWallet)) ||
						combinedCapabilities.includes(InternalCapability.FedexDirect)
					);
				}
				return true;
			}
		);

		setConfigured(filteredConfiguredCarriers);
	};

	const getSharedCapabilitiesAndCarriersForBulkUpdate = () => {
		const location = {
			capabilities: [],
			configuredCarriers: [],
		};

		location.Capabilities = Util.getSharedItemsFromArray(
			enterpriseLocations.map(({ Capabilities }) => Capabilities)
		);
		location.InternalCapabilities = Util.getSharedItemsFromArray(
			enterpriseLocations.map(
				({ InternalCapabilities }) => InternalCapabilities
			)
		);
		location.configuredCarriers = Util.getSharedObjectsFromArray(
			enterpriseLocations.map(
				({ ConfiguredCarriers = [] }) => ConfiguredCarriers
			),
			'carrier'
		);

		return location;
	};

	useConstructor(() => {
		// Load the selected location from localstorage since we can't use Session
		const storedLocation = JSON.parse(
			localStorage.getItem('selectedEnterpriseLocation')
		);
		if (storedLocation) {
			setEntLocation(storedLocation);
		}

		// Bulk Enterprise Location Update
		if (!isEmpty(enterpriseLocations)) {
			setCapableAndConfiguredCarriers(
				getSharedCapabilitiesAndCarriersForBulkUpdate()
			);
		}
		// Single Enterprise Location Update
		else if (!isEmpty(entLocation)) {
			setCapableAndConfiguredCarriers(entLocation);

			// Save the selected location in localstorage since we can't use Session
			localStorage.setItem(
				'selectedEnterpriseLocation',
				JSON.stringify(entLocation)
			);
		}
		// default
		else {
			setCapableAndConfiguredCarriers();

			const id = Session.subscribe(() => {
				setCapableAndConfiguredCarriers();
			});

			setSubscriberId(id);
		}
	});

	useEffect(() => () => Session.unsubscribe(subscriberId), []);

	useEffect(() => {
		if (!isUspsBusinessCheckInProgress) {
			setCapableAndConfiguredCarriers();
		}
	}, [
		uspsOnboardingStatus,
		isUspsBusinessEligible,
		isUspsBusinessCheckInProgress,
	]);

	const completionCallback = (
		success,
		carrier,
		locationsLength,
		carrierType
	) => {
		if (success) {
			Session.loadAccountInfo();

			if (enterpriseLocation && carrier) {
				setConfigured([...configured, carrier]);
			}
		}
		addCarrierCallback(success, carrier, locationsLength);
		setAction(null);
		setSelectedCarrier(null);
		setIsFedExAddFlow(false);

		if (carrier === Carrier.FedEx) {
			setIsFedExResellerBannerVisible(false);
		}

		if (carrierType === 'FedExReseller') {
			showModal({
				component: ({ modalId }) => (
					<StatusModal
						modalId={modalId}
						header="Connection Request Submitted"
						title="Your FedEx request is submitted and under review."
						description="Once approved, typically within 24 hours, you'll be able to access FedEx services."
						dataqa="fedex-connection-success-modal"
						iconType="success"
					/>
				),
			});
		} else if (carrierType === 'FedExDirect') {
			showModal({
				component: ({ modalId }) => (
					<StatusModal
						modalId={modalId}
						header="Account Connected"
						description={`Your FedEx account has been successfully connected.`}
						dataqa="fedex-byoa-connected-modal"
						iconType="success"
					/>
				),
			});
		}
	};

	const addCarrierOnClick = () => {
		setAction(ManageCarrierAction.add);
	};

	const editCarrierOnClick = () => {
		setAction(ManageCarrierAction.edit);
	};

	const monoauthAddCarrier = async () => {
		const response = await Communication.monoauthAddCarrier({
			Code: code,
			AuthState: authState,
		});

		const isReauthFlow = !isEmpty(carrierDetails);

		if (Communication.isSuccessful(response)) {
			Session.loadAccountInfo();
			setAction(null);
			const title = `${carrierType.toUpperCase()} Account Authenticated!`;
			if (isReauthFlow) {
				showModal({
					component: MessageModal,
					data: {
						title,
						description: `You can resume use of your ${carrierType.toUpperCase()} account.`,
					},
				});
			} else {
				setConfigured([...configured, carrierType]);

				showModal({
					component: MessageModal,
					data: {
						title,
						description: `Your UPS account and rates are available in Stamps.com.`,
					},
				});

				addCarrierCallback(true, carrierType, null);
			}
		} else if (
			response.ErrorCode.toString() === ErrorCodes.swsim.UPS_ADD_CARRIER_ERROR
		) {
			showError({
				component: MessageModal,
				data: {
					title: 'Unable to add UPS account',
					description: 'Please review the provided information and try again.',
				},
			});
		} else {
			showError({ response });
		}
	};

	const isCarrierEditEligible = selectedCarrier => {
		if (selectedCarrier?.value === Carrier.UPS) {
			const carrier = carrierDetails?.[selectedCarrier.value];

			return carrier?.userOwnedAccount === true;
		}
		if (
			canConnectToUspsBusiness &&
			selectedCarrier?.value === Carrier.USPSBusiness
		) {
			return true;
		}

		if (
			selectedCarrier?.value === Carrier.FedEx &&
			Session.hasCapability(InternalCapability.FedexWallet) &&
			Session.hasCapability(InternalCapability.FedexDirect)
		) {
			return true;
		}
		return false;
	};

	const isCarrierDeleteEligible = selectedCarrier => {
		if (selectedCarrier?.value === Carrier.FedEx) {
			return true;
		}
		return false;
	};

	useEffect(() => {
		if (isString(code) && isString(authState)) {
			monoauthAddCarrier();
		}
	}, []);

	useEffect(() => {
		if (canConnectToUspsBusiness && isUspsBusinessEligible) {
			refetch();
		}
	}, []);

	const checkUps =
		!capable.includes(Carrier.UPS) || configured.includes(Carrier.UPS);
	const checkDhlx =
		!capable.includes(Carrier.DHLExpress) ||
		configured.includes(Carrier.DHLExpress);
	const checkFedEx =
		!capable.includes(Carrier.FedEx) || configured.includes(Carrier.FedEx);
	const isAddDisabled = checkUps && checkDhlx && checkFedEx;
	const isEditEligible = isCarrierEditEligible(selectedCarrier);
	const isCarrierDeletable = isCarrierDeleteEligible(selectedCarrier);
	const isSingleLocationSelected =
		isObject(enterpriseLocation) && Object.keys(enterpriseLocation).length > 0;
	const { DismissedUspsBusinessApprovalMessage } = Session.getPreferences();

	// array of objects with button configurations
	const buttons = [
		{
			label: 'Add',
			icon: 'add-icon',
			onClick: addCarrierOnClick,
			disabled: isAddDisabled,
		},
		{
			label: 'Edit',
			onClick: editCarrierOnClick,
			disabled: !isEditEligible,
		},
		{
			label: 'Disconnect',
			onClick: () => {
				showModal({
					component: ({ modalId }) => (
						<FedExDisconnect
							modalId={modalId}
							carrier={selectedCarrier}
							completionCallback={completionCallback}
						/>
					),
				});
			},
			disabled: !isCarrierDeletable,
		},
	];

	if (canConnectToUspsBusiness) {
		if (uspsBusinessError) {
			if (uspsBusinessError.data || uspsBusinessError.response)
				showError(uspsBusinessError);
			else
				showError({
					data: {
						title: 'Error',
						description: uspsBusinessError,
					},
				});
			setUspsBusinessError(null);
		}
		if (isUspsBusinessCheckInProgress) {
			return <Spinner msg={'Checking USPS Business Access...'} />;
		}

		if (
			shouldShowUspsBusiness ||
			(selectedCarrier?.value === Carrier.USPSBusiness &&
				(action === ManageCarrierAction.edit ||
					action === ManageCarrierAction.add))
		) {
			return (
				<ConnectUSPSBusinessAccount
					onboardingStatus={uspsOnboardingStatus}
					userInfoToken={userInfoToken}
					onDismiss={hideUspsBusinessConnect}
					refetch={refetch}
					redirectUri={uspsRedirectUri}
					clearUserInfoToken={clearUserInfoToken}
				/>
			);
		}
	}

	const onCloseInlineMessage = () => {
		if (uspsBannerType === 'information') {
			Session.savePreferences([
				{
					Name: 'DismissedUspsBusinessApprovalMessage',
					Value: '1',
				},
			]);
		}
	};

	if (
		(action === ManageCarrierAction.edit &&
			selectedCarrier.value === Carrier.FedEx) ||
		isFedExAddFlow
	) {
		return (
			<AddFedExAccount
				enterpriseLocations={enterpriseLocations || null}
				completionCallback={completionCallback}
				custId={entLocation ? entLocation.CustId : null}
				isEditing={action === ManageCarrierAction.edit}
				hasUserOwnedAccount={
					carrierDetails?.[Carrier.FedEx] &&
					carrierDetails?.[Carrier.FedEx].userOwnedAccount
				}
			/>
		);
	}

	return (
		<>
			{/* default view displaying the toolbar + grid */}
			{action === ManageCarrierAction.null && (
				<div className="common-carrier-manage-carrier">
					<Section>
						<WrappedToolbar buttons={buttons} dataqa="managecarrier" />
						<SectionBody>
							<p className="header">Manage your linked Carrier accounts.</p>
							{canConnectToUspsBusiness &&
								uspsBannerType !== 'information' &&
								isUspsBusinessEligible && (
									<UspsBusinessBanner
										type={uspsBannerType}
										onCheckGuidelinesClick={showUspsBusiness}
										onLearnMoreClick={() => {
											// Could open external link or modal here
											// TODO: add link to learn more about USPS Business Accounts when COP documentation is ready
										}}
									/>
								)}
							{canConnectToUspsBusiness &&
								isUspsBusinessEligible &&
								uspsBannerType === 'information' &&
								DismissedUspsBusinessApprovalMessage !== '1' && (
									<UspsBusinessBanner
										type={uspsBannerType}
										canClose={true}
										onClose={onCloseInlineMessage}
										onCheckGuidelinesClick={showUspsBusiness}
										onLearnMoreClick={() => {
											// Could open external link or modal here
											// TODO: add link to learn more about USPS Business Accounts when COP documentation is ready
										}}
									/>
								)}
							{canAddFedexReseller && isFedExResellerBannerVisible && (
								<FedExResellerMessage
									onClose={() => setIsFedExResellerBannerVisible(false)}
									onAddFedExClick={() => {
										setIsFedExAddFlow(true);
									}}
								/>
							)}
							{canAddFedexReseller && isFedexPendingBannerVisible && (
								<FedExPendingMessage />
							)}
							<GridPanel
								rowData={carrierRowData(configured)}
								columnDefs={[
									{
										headerName: 'Carrier',
										cellRendererFramework: CarrierCellRenderer,
										sortable: false,
										resizable: false,
										flex: 1,
										suppressSizeToFit: false, // to fill the entire available space
										menuTabs: [], // to hide the column menu
										cellClass: ({ data: { carrier } }) => {
											const { value } = carrier || '';
											const carrierLogoCls = value.replace(/_/g, '-');

											return `${carrierLogoCls}-logo manage-carrier-row carrier-logo`;
										},
									},
								]}
								onSelectionChanged={selection => {
									// Add Segment tracking for when use selects a carrier
									Analytics.trackSegmentEvent({
										trackingData: {
											field_type: 'selection',
											field_name_dataqa: `manage-carriers-carrier-selection-${selection[0].data.carrier}`,
										},
										eventType: SegmentEventTypes.UserInteraction,
									});

									setSelectedCarrier(selection[0].data.carrier);
								}}
								showPagination={false}
								dataqa="managecarrier"
							/>
							<WrappedTooltip
								target="usps-business-help-icon"
								position="top"
								tooltip="USPS Business is for accounts that have access to discounted commercial rates, such as those through a Negotiated Service Agreement (NSA)."
							/>
						</SectionBody>
					</Section>
				</div>
			)}
			{/* clicking the Add button displays the carrier selection grid */}

			{action === ManageCarrierAction.edit &&
				selectedCarrier.value === Carrier.UPS && (
					<AddUpsCarrier
						editCarrierDetails={carrierDetails[selectedCarrier.value]}
						completionCallback={completionCallback}
						action={action}
					/>
				)}

			{action === ManageCarrierAction.add && (
				<AddNewCarrier
					capable={capable}
					configured={configured}
					custId={entLocation ? entLocation.CustId : null}
					enterpriseLocations={enterpriseLocations || null}
					isSingleLocationSelected={isSingleLocationSelected}
					completionCallback={completionCallback}
				/>
			)}
		</>
	);
};
